// src/pages/Accauntlar.js
import React, { useEffect, useState } from "react";
import api from "../api";
import "./Accauntlar.css";

// Backend va frontend bir xil ishlatadigan sahifa ruxsatlari
// Sidebar dagi barcha 15 ta menu — alohida nazorat
const PERM_KEYS = [
  { key: "Dashboard",     label: "Dashboard" },
  { key: "Savdo",         label: "Sotuv" },
  { key: "CheklarTarixi", label: "Cheklar tarixi" },
  { key: "Buyurtmalar",   label: "Buyurtmalar" },
  { key: "BozorTarix",    label: "Bozor tarix" },
  { key: "Vozvrat",       label: "Vozvrat" },
  { key: "Ombor",         label: "Ombor" },
  { key: "Kassa",         label: "Kassa" },
  { key: "Hisobkitob",    label: "Hisob-kitob" },
  { key: "Accauntlar",    label: "Accauntlar" },
  { key: "Maxsulotqosh",  label: "Maxsulot qo’shish" },
  { key: "MaxsulotTarix", label: "Maxsulot tarixi" },
  { key: "Mijozlar",      label: "Mijozlar" },
  { key: "Sozlamalar",    label: "Sozlamalar" },
  { key: "Foyda",         label: "Foyda" },
];

// Backenddan kelgan userni normallashtirish
function normalizeUser(raw = {}) {
  return {
    id: raw.id ?? null,
    username: raw.username ?? "",
    amali: raw.amali ?? "",
    pin_code: raw.pin_code ?? "",
    role: raw.role || "cashier",
    perms: raw.perms || {},
    allowed_sklad_ids: Array.isArray(raw.allowed_sklad_ids)
      ? raw.allowed_sklad_ids.map((x) => Number(x)).filter((x) => Number.isFinite(x) && x > 0)
      : [],
  };
}

// Yangi bo'sh user shabloni
function emptyUser() {
  return {
    id: null,
    username: "",
    amali: "",
    pin_code: "",
    role: "cashier",
    perms: {},
    allowed_sklad_ids: [],
  };
}

function allowedWarehouseNames(user, warehouses) {
  const ids = new Set((user?.allowed_sklad_ids || []).map((x) => Number(x)));
  if (!ids.size) return "Barchasi";
  const names = warehouses
    .filter((w) => ids.has(Number(w.id)))
    .map((w) => w.nom || w.name || `#${w.id}`);
  return names.length ? names.join(", ") : "Tanlanmagan";
}

export default function Accauntlar() {
  const [rows, setRows] = useState([]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // PIN ko‘rinishi (ro‘yxatda)
  const [showPins, setShowPins] = useState(false);

  // Modal holati
  const [editIndex, setEditIndex] = useState(null);
  const [editDraft, setEditDraft] = useState(null);
  const [modalShowPin, setModalShowPin] = useState(true);
  const [modalError, setModalError] = useState("");

  // ====== Ro‘yxatni serverdan yuklash ======
  useEffect(() => {
    let cancelled = false;

    async function fetchUsers() {
      setLoading(true);
      setError("");
      setSuccess("");
      try {
        const [accountsRes, warehousesRes] = await Promise.all([
          api.get("/accounts/"),
          api.get("/sklad/"),
        ]);
        if (cancelled) return;
        const list = Array.isArray(accountsRes.data)
          ? accountsRes.data
          : accountsRes.data?.results || [];
        const warehouseList = Array.isArray(warehousesRes.data)
          ? warehousesRes.data
          : warehousesRes.data?.results || [];
        setRows(list.map(normalizeUser));
        setWarehouses(warehouseList);
      } catch (err) {
        console.error("Accounts fetch error:", err);
        if (!cancelled) {
          setError("Foydalanuvchilar ro‘yxatini yuklashda xato.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, []);

  // ====== Toolbar tugmalari ======

  // Yangi accaunt qo‘shish – faqat state ga qo‘shadi, serverga keyin saqlaymiz
  const handleAddUser = () => {
    setError("");
    setSuccess("");
    const newUser = emptyUser();
    const newIndex = rows.length;
    setRows([...rows, newUser]);
    setEditIndex(newIndex);
    setEditDraft({ ...newUser });
    setModalShowPin(true);
    setModalError("");
  };

  // Barchasini saqlash: yangi va mavjudlarni POST / PUT qilib yuborish
  const saveAll = async () => {
    setSaving(true);
    setError("");
    setSuccess("");

    try {
      const nextRows = [];

      for (const row of rows) {
        const payload = {
          username: row.username,
          amali: row.amali,
          pin_code: row.pin_code,
          role: row.role || "cashier",
          perms: row.perms || {},
          allowed_sklad_ids: row.allowed_sklad_ids || [],
        };

        if (!payload.username || !payload.username.trim()) {
          throw new Error("Username bo‘sh bo‘lishi mumkin emas.");
        }

        if (row.id) {
          // mavjud user yangilanadi
          const res = await api.put(`/accounts/${row.id}/`, payload);
          nextRows.push(normalizeUser(res.data));
        } else {
          // yangi user yaratiladi
          const res = await api.post("/accounts/", payload);
          nextRows.push(normalizeUser(res.data));
        }
      }

      setRows(nextRows);
      setSuccess("Barcha o‘zgarishlar saqlandi.");
    } catch (err) {
      console.error("Accounts save error:", err);
      const msg =
        err?.response?.data?.detail ||
        err?.response?.data ||
        err.message ||
        "Saqlashda xato.";
      setError(typeof msg === "string" ? msg : "Saqlashda xato.");
    } finally {
      setSaving(false);
    }
  };

  // Perms chip ustida bosilganda yoqib/o‘chiradi
  const togglePerm = (index, key) => {
    setRows((prev) => {
      const copy = [...prev];
      const u = { ...copy[index] };
      const perms = { ...(u.perms || {}) };
      perms[key] = !perms[key];
      u.perms = perms;
      copy[index] = u;
      return copy;
    });
  };

  // Accauntni o‘chirish (bor bo‘lsa backenddan ham)
  const removeRow = async (index) => {
    const row = rows[index];
    if (row && row.id) {
      const ok = window.confirm(
        `"${row.username}" accauntini o‘chirishni xohlaysizmi?`
      );
      if (!ok) return;
      try {
        await api.delete(`/accounts/${row.id}/`);
      } catch (err) {
        console.error("Account delete error:", err);
        setError("Accauntni o‘chirishda xato.");
        return;
      }
    }
    setRows((prev) => prev.filter((_, i) => i !== index));
    setSuccess("");
    setError("");
  };

  // Qatorni tahrirlash uchun modalni ochish
  const openEditModal = (index) => {
    const row = rows[index];
    if (!row) return;
    setEditIndex(index);
    setEditDraft({ ...row });
    setModalShowPin(true);
    setModalError("");
  };

  // Modalni yopish
  const closeModal = () => {
    setEditIndex(null);
    setEditDraft(null);
    setModalError("");
  };

  // Modal ichida maydonlarni o‘zgartirish
  const updateDraftField = (field, value) => {
    setEditDraft((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const toggleDraftWarehouse = (warehouseId) => {
    setEditDraft((prev) => {
      if (!prev) return prev;
      const current = new Set((prev.allowed_sklad_ids || []).map((x) => Number(x)));
      const id = Number(warehouseId);
      if (current.has(id)) current.delete(id);
      else current.add(id);
      return {
        ...prev,
        allowed_sklad_ids: Array.from(current).sort((a, b) => a - b),
      };
    });
  };

  // Modal "O‘zgarishlarni qo‘llash" – faqat frontend ro‘yxatga yozadi
  const applyDraftToRows = () => {
    if (!editDraft) return;
    if (!editDraft.username || !editDraft.username.trim()) {
      setModalError("Username bo‘sh bo‘lishi mumkin emas.");
      return;
    }
    if (editIndex == null) return;

    setRows((prev) => {
      const copy = [...prev];
      copy[editIndex] = { ...copy[editIndex], ...editDraft };
      return copy;
    });

    closeModal();
  };

  // PIN matnini ko‘rinishini o‘zgartirish (ro‘yxatda)
  const toggleShowPins = () => {
    setShowPins((v) => !v);
  };

  // ====== Render ======
  return (
    <div className="acc-wrap">
      {/* Toolbar */}
      <div className="acc-toolbar fr-panel">
        <div>
          <h2 style={{ margin: 0, marginBottom: 4 }}>Accauntlar</h2>
          <div style={{ fontSize: 13, color: "var(--muted)" }}>
            Jami: {rows.length} ta accaunt
          </div>
        </div>
        <div className="btns-inline">
          <button type="button" className="btn-add" onClick={handleAddUser}>
            Yangi accaunt <span className="plus">+</span>
          </button>
          <button
            type="button"
            className="btn-primary"
            onClick={saveAll}
            disabled={saving || loading || rows.length === 0}
          >
            {saving ? "Saqlanmoqda..." : "Barchasini saqlash"}
          </button>
        </div>
      </div>

      {/* Panel */}
      <div className="acc-panel fr-card">
        <div className="acc-head">
          <div className="grid">
            <div>Foydalanuvchi / Amali</div>
            <div>Ruxsatlar</div>
            <div style={{ textAlign: "right" }}>Login ma’lumotlari</div>
          </div>
        </div>
        <div className="acc-sep" />

        {error && (
          <div style={{ color: "red", padding: "4px 8px", fontSize: 13 }}>
            {error}
          </div>
        )}
        {success && (
          <div style={{ color: "green", padding: "4px 8px", fontSize: 13 }}>
            {success}
          </div>
        )}

        {loading ? (
          <div className="empty">Yuklanmoqda...</div>
        ) : rows.length === 0 ? (
          <div className="empty">
            Hozircha accauntlar yo‘q. “Yangi accaunt” tugmasini bosing.
          </div>
        ) : (
          <div className="acc-list">
            {rows.map((u, idx) => (
              <div className="acc-row" key={u.id ?? `row-${idx}`}>
                <div className="grid">
                  {/* Chap ustun: foydalanuvchi va amali */}
                  <div>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: 6,
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 700 }}>
                          {u.username || (
                            <span style={{ opacity: 0.6 }}>
                              (username yo‘q)
                            </span>
                          )}
                        </div>
                        <div
                          className="mono"
                          style={{
                            fontSize: 12,
                            color: "var(--muted)",
                            marginTop: 2,
                          }}
                        >
                          ID: {u.id ?? "—"}
                        </div>
                      </div>
                      <span className="role-badge">
                        {u.role || "cashier"}
                      </span>
                    </div>

                    {u.amali && (
                      <div style={{ fontSize: 14, marginBottom: 6 }}>
                        {u.amali}
                      </div>
                    )}

                    <div className="warehouse-line">
                      <span className="warehouse-label">Skladlar:</span>{" "}
                      <span>{allowedWarehouseNames(u, warehouses)}</span>
                    </div>

                    <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                      <button
                        type="button"
                        className="btn-light"
                        onClick={() => openEditModal(idx)}
                      >
                        Tahrirlash
                      </button>
                      <button
                        type="button"
                        className="btn-light"
                        onClick={() => removeRow(idx)}
                      >
                        O‘chirish
                      </button>
                    </div>
                  </div>

                  {/* O‘rta ustun: perms chiplar */}
                  <div className="col-perms">
                    {PERM_KEYS.map((p) => (
                      <div
                        key={p.key}
                        className="perm-chip"
                        onClick={() => togglePerm(idx, p.key)}
                      >
                        <span
                          className={
                            "dot tiny " +
                            (u.perms && u.perms[p.key] ? "on" : "off")
                          }
                        />
                        <span className="perm-text">{p.label}</span>
                      </div>
                    ))}
                  </div>

                  {/* O‘ng ustun: credentiallar */}
                  <div className="col-cred">
                    <div className="cred">
                      <div className="pw-line">
                        <span className="mono">
                          PIN:{" "}
                          {showPins
                            ? u.pin_code || "—"
                            : u.pin_code
                            ? "•".repeat(u.pin_code.length)
                            : "—"}
                        </span>
                        <button
                          type="button"
                          className="eye-btn"
                          onClick={toggleShowPins}
                          title={
                            showPins ? "PIN ni yashirish" : "PIN ni ko‘rsatish"
                          }
                        >
                          {showPins ? "🙈" : "👁"}
                        </button>
                      </div>
                      <div
                        className="mono"
                        style={{ marginTop: 6, fontSize: 13 }}
                      >
                        Role: {u.role || "cashier"}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Modal: yangi / tahrirlash */}
      {editDraft && (
        <div className="modal-backdrop">
          <div className="modal">
            <div className="modal-head">
              <div className="modal-title">
                {editDraft.id ? "Accauntni tahrirlash" : "Yangi accaunt"}
              </div>
              <button
                type="button"
                className="btn-light"
                onClick={closeModal}
              >
                Yopish
              </button>
            </div>

            <div className="modal-body">
              {modalError && (
                <div style={{ color: "red", fontSize: 13 }}>{modalError}</div>
              )}

              <div className="form-row">
                <label>Username</label>
                <input
                  className="input"
                  type="text"
                  value={editDraft.username}
                  onChange={(e) =>
                    updateDraftField("username", e.target.value)
                  }
                  placeholder="login uchun username"
                />
                <div className="hint">
                  Faqat lotin harflari va raqamlar bo‘lishi tavsiya etiladi.
                </div>
              </div>

              <div className="form-row">
                <label>Amali (lavozim)</label>
                <input
                  className="input"
                  type="text"
                  value={editDraft.amali}
                  onChange={(e) => updateDraftField("amali", e.target.value)}
                  placeholder="Masalan: Terminal-1 kassir"
                />
                <div className="hint">
                  Erkin matn. Accauntlar ro‘yxatida ko‘rinadi.
                </div>
              </div>

              <div className="form-row">
                <label>Role</label>
                <select
                  className="input"
                  value={editDraft.role || "cashier"}
                  onChange={(e) => updateDraftField("role", e.target.value)}
                >
                  <option value="admin">admin</option>
                  <option value="cashier">cashier</option>
                </select>
                <div className="hint">
                  admin: ruxsat bo?sh ? hammasi; belgilansa ? shu bo?limlar yashiriladi. cashier: ruxsat bo?sh ? hech narsa; belgilansa ? shu bo?limlar.
                </div>
              </div>

              <div className="form-row">
                <label>Sklad ruxsatlari</label>
                <div className="warehouse-box">
                  {warehouses.map((w) => {
                    const id = Number(w.id);
                    const checked = (editDraft.allowed_sklad_ids || []).includes(id);
                    return (
                      <label key={w.id} className="warehouse-item">
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleDraftWarehouse(id)}
                        />
                        <span>{w.nom || w.name}</span>
                      </label>
                    );
                  })}
                </div>
                <div className="hint">
                  Hech narsa tanlanmasa, account barcha skladlarga to'liq kira oladi.
                  Cheklangan admin bunday account yaratolmaydi.
                </div>
              </div>

              <div className="form-row">
                <label>Sahifa ruxsatlari</label>
                <div className="modal-perms-hint">
                  <span className="hint">
                    <b>admin:</b> belgilangan = o'sha sahifa yashiriladi. &nbsp;
                    <b>cashier:</b> belgilangan = faqat o'sha sahifalar ko'rinadi.
                  </span>
                </div>
                <div className="modal-perms-grid">
                  {PERM_KEYS.map((p) => {
                    const on = !!(editDraft.perms && editDraft.perms[p.key]);
                    return (
                      <div
                        key={p.key}
                        className={"modal-perm-chip" + (on ? " on" : "")}
                        onClick={() =>
                          setEditDraft((prev) => {
                            if (!prev) return prev;
                            const perms = { ...(prev.perms || {}) };
                            perms[p.key] = !perms[p.key];
                            return { ...prev, perms };
                          })
                        }
                      >
                        <span className={"dot tiny " + (on ? "on" : "off")} />
                        {p.label}
                      </div>
                    );
                  })}
                </div>
              </div>

              <div className="form-row">
                <label>PIN-kod</label>
                <div className="pw-field">
                  <input
                    className="input"
                    type={modalShowPin ? "text" : "password"}
                    value={editDraft.pin_code}
                    onChange={(e) =>
                      updateDraftField("pin_code", e.target.value)
                    }
                    placeholder="harflar va raqamlar aralash, 1–10 belgi"
                  />
                  <button
                    type="button"
                    className="eye-btn"
                    onClick={() => setModalShowPin((v) => !v)}
                    title={
                      modalShowPin ? "PIN ni yashirish" : "PIN ni ko‘rsatish"
                    }
                  >
                    {modalShowPin ? "🙈" : "👁"}
                  </button>
                </div>
                <div className="hint">
                  Bu kod foydalanuvchi uchun login kodi bo‘ladi.
                </div>
              </div>
            </div>

            <div className="modal-foot">
              <button
                type="button"
                className="btn-primary"
                onClick={applyDraftToRows}
              >
                O‘zgarishlarni qo‘llash
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
